/**
 * IndexedDB Backend - v13 Section 8.13
 *
 * Browser-based storage backend using IndexedDB via the idb library.
 * Suitable for PWA deployments.
 *
 * @see docs/architecture/extracts/storage-backends-8.13.md
 */
import { openDB } from 'idb';
/**
 * IndexedDBBackend - Browser storage using IndexedDB
 */
export class IndexedDBBackend {
    constructor(config) {
        this.dbPromise = null;
        this.dbName = config?.dbName ?? 'ownyou_store';
    }
    /**
     * Get or create database connection
     */
    async getDB() {
        if (!this.dbPromise) {
            this.dbPromise = openDB(this.dbName, 1, {
                upgrade(db) {
                    const store = db.createObjectStore('memories', {
                        keyPath: 'key',
                    });
                    // Composite index for namespace + user queries
                    store.createIndex('by-namespace-user', ['namespace', 'userId']);
                    store.createIndex('by-namespace', 'namespace');
                    store.createIndex('by-user', 'userId');
                },
            });
        }
        return this.dbPromise;
    }
    /**
     * Create composite key from namespace, userId, and key
     */
    makeKey(namespace, userId, key) {
        return `${namespace}::${userId}::${key}`;
    }
    async put(namespace, userId, key, value) {
        const db = await this.getDB();
        const compositeKey = this.makeKey(namespace, userId, key);
        const now = Date.now();
        await db.put('memories', {
            key: compositeKey,
            namespace,
            userId,
            value,
            createdAt: now,
            updatedAt: now,
        });
    }
    async get(namespace, userId, key) {
        const db = await this.getDB();
        const compositeKey = this.makeKey(namespace, userId, key);
        const record = await db.get('memories', compositeKey);
        return record ? record.value : null;
    }
    async delete(namespace, userId, key) {
        const db = await this.getDB();
        const compositeKey = this.makeKey(namespace, userId, key);
        const exists = await db.get('memories', compositeKey);
        if (!exists)
            return false;
        await db.delete('memories', compositeKey);
        return true;
    }
    async list(namespace, userId, limit, offset) {
        const db = await this.getDB();
        const index = db.transaction('memories').store.index('by-namespace-user');
        const items = [];
        let skipped = 0;
        // Use cursor for efficient iteration
        let cursor = await index.openCursor(IDBKeyRange.only([namespace, userId]));
        while (cursor) {
            if (skipped < offset) {
                skipped++;
            }
            else if (items.length < limit) {
                items.push(cursor.value.value);
            }
            else {
                break;
            }
            cursor = await cursor.continue();
        }
        return items;
    }
    async exists(namespace, userId, key) {
        const db = await this.getDB();
        const compositeKey = this.makeKey(namespace, userId, key);
        const record = await db.get('memories', compositeKey);
        return record !== undefined;
    }
    async getStats(namespace, userId) {
        const db = await this.getDB();
        const index = db.transaction('memories').store.index('by-namespace-user');
        let count = 0;
        let oldest = Infinity;
        let newest = 0;
        let cursor = await index.openCursor(IDBKeyRange.only([namespace, userId]));
        while (cursor) {
            count++;
            oldest = Math.min(oldest, cursor.value.createdAt);
            newest = Math.max(newest, cursor.value.createdAt);
            cursor = await cursor.continue();
        }
        return {
            namespace,
            count,
            oldestRecord: count > 0 ? oldest : undefined,
            newestRecord: count > 0 ? newest : undefined,
        };
    }
    async clear(namespace, userId) {
        const db = await this.getDB();
        if (!namespace && !userId) {
            // Clear all
            await db.clear('memories');
            return;
        }
        const tx = db.transaction('memories', 'readwrite');
        const store = tx.store;
        if (namespace && userId) {
            // Clear specific namespace and user
            const index = store.index('by-namespace-user');
            let cursor = await index.openCursor(IDBKeyRange.only([namespace, userId]));
            while (cursor) {
                await cursor.delete();
                cursor = await cursor.continue();
            }
        }
        else if (namespace) {
            // Clear all users in namespace
            const index = store.index('by-namespace');
            let cursor = await index.openCursor(IDBKeyRange.only(namespace));
            while (cursor) {
                await cursor.delete();
                cursor = await cursor.continue();
            }
        }
        else if (userId) {
            // Clear all namespaces for user
            const index = store.index('by-user');
            let cursor = await index.openCursor(IDBKeyRange.only(userId));
            while (cursor) {
                await cursor.delete();
                cursor = await cursor.continue();
            }
        }
        await tx.done;
    }
    async close() {
        if (this.dbPromise) {
            const db = await this.dbPromise;
            db.close();
            this.dbPromise = null;
        }
    }
}
